import { useState, useEffect } from 'react';
import { Eye, FileText, Image, Loader2, Pencil, Trash2, X, ZoomIn } from 'lucide-react';
import { ExpenseAttachmentButton } from './ExpenseAttachmentButton';
import {
  type ExpenseDocument,
  type ExpenseItemWithRelations,
  type User,
  isAdmin,
} from '@peles/shared';
import { statusBadgeStyle } from '../common';
import { useDialog } from '../../context/DialogContext';
import { useViewMode } from '../../context/ViewModeContext';
import { useExpenseDocuments } from '../../hooks/useExpenseDocuments';
import { DocumentPreviewModal } from './ExpenseDocumentsSection';

interface ExpenseTableProps {
  items: ExpenseItemWithRelations[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  onDelete?: (id: string) => void;
  /** When set, rows show a zoom button that opens the itemized list scoped to that expense. */
  onZoomItems?: (item: ExpenseItemWithRelations) => void;
  isLoading: boolean;
  currentUser?: User;
  showEventName?: boolean;
}

function formatCurrency(amount: number): string {
  return `$${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatDate(date: Date | string | null): string {
  if (!date) return '—';
  return new Date(date).toLocaleDateString();
}

function isOverdue(date: Date | string | null): boolean {
  if (!date) return false;
  return new Date(date) < new Date();
}

function getClientStatusBadge(item: ExpenseItemWithRelations): {
  label: string;
  className: string;
  title: string;
} | null {
  if (!item.isProducerFunded) return null;
  const balance = item.clientBalance;
  if (balance <= 0.005) {
    return {
      label: 'paid by client',
      className: 'bg-green-100 text-green-700',
      title: `Client has paid ${formatCurrency(item.clientPaid)} toward this expense.`,
    };
  }
  if (item.clientPaid > 0.005) {
    return {
      label: `partial • ${formatCurrency(balance)} owed`,
      className: 'bg-amber-100 text-amber-700',
      title: `Client has paid ${formatCurrency(item.clientPaid)} of ${formatCurrency(item.price)}.`,
    };
  }
  return {
    label: `${item.clientChargeType === 'income' ? 'income' : 'reimbursement'} • ${formatCurrency(balance)} owed`,
    className: 'bg-red-100 text-red-700',
    title:
      item.clientChargeType === 'income'
        ? 'Owed to producer for production services.'
        : 'Producer fronted this; awaiting client reimbursement.',
  };
}

// Shared with the planning status badge so colors render identically.
const getStatusBadgeStyle = statusBadgeStyle;

export interface ExpenseDocPreviewProps {
  expenseItemId: string;
  onClose: () => void;
}

export function ExpenseDocPreview({ expenseItemId, onClose }: ExpenseDocPreviewProps) {
  const { data: docs = [], isLoading } = useExpenseDocuments(expenseItemId);
  const [previewDoc, setPreviewDoc] = useState<ExpenseDocument | null>(null);

  useEffect(() => {
    if (!isLoading && docs.length === 1 && !previewDoc) {
      setPreviewDoc(docs[0]);
    }
  }, [isLoading, docs, previewDoc]);

  if (previewDoc) {
    return (
      <DocumentPreviewModal
        document={previewDoc}
        onClose={() => {
          if (docs.length <= 1) onClose();
          else setPreviewDoc(null);
        }}
      />
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-lg shadow-xl p-4 w-80 max-w-full mx-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-gray-900">Documents</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 p-1"
            aria-label="Close documents"
          >
            <X size={16} />
          </button>
        </div>
        {isLoading ? (
          <div className="flex items-center justify-center gap-2 py-4">
            <Loader2 size={16} className="animate-spin text-primary-600" />
            <span className="text-sm text-gray-600">Loading...</span>
          </div>
        ) : docs.length === 0 ? (
          <p className="text-sm text-gray-400 py-4 text-center">No documents attached</p>
        ) : (
          <ul className="space-y-2" data-testid="doc-selector-list">
            {docs.map((doc) => (
              <li
                key={doc.id}
                className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-md hover:bg-gray-100 cursor-pointer"
                onClick={() => setPreviewDoc(doc)}
              >
                {doc.mimeType.startsWith('image/') ? (
                  <Image size={14} className="text-blue-500 flex-shrink-0" />
                ) : (
                  <FileText size={14} className="text-red-500 flex-shrink-0" />
                )}
                <span className="text-sm text-gray-700 truncate flex-1">{doc.fileName}</span>
                <Eye size={14} className="text-gray-400 flex-shrink-0" />
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

export function ExpenseTable({
  items,
  selectedId,
  onSelect,
  onDelete,
  onZoomItems,
  isLoading,
  currentUser,
  showEventName = false,
}: ExpenseTableProps) {
  const { confirm } = useDialog();

  if (isLoading) {
    return <div className="text-gray-500 text-sm py-8 text-center">Loading expenses...</div>;
  }

  if (items.length === 0) {
    return <div className="text-gray-500 text-sm py-8 text-center">No expenses found</div>;
  }

  const { isMobile } = useViewMode();
  const currentUserIsAdmin = currentUser && isAdmin(currentUser.systemRole);

  if (isMobile) {
    return (
      <>
        <div className="space-y-3">
          {items.map((item) => (
            <div
              key={item.id}
              onClick={() => onSelect(item.id)}
              className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                selectedId === item.id
                  ? 'border-primary-500 bg-primary-50'
                  : 'border-gray-200 hover:bg-gray-50'
              }`}
            >
              {showEventName && (item.eventName || item.eventLogoUrl) && (
                <div className="flex items-center gap-2 mb-2 text-xs text-gray-700">
                  {item.eventLogoUrl && (
                    <img
                      src={item.eventLogoUrl}
                      alt=""
                      className="w-5 h-5 rounded object-cover border border-gray-200 bg-white flex-shrink-0"
                    />
                  )}
                  <span className="font-medium truncate">{item.eventName}</span>
                </div>
              )}
              <div className="flex justify-between items-start mb-1">
                <span className="font-medium text-sm">
                  {item.itemName || item.vendorCategoryName}
                </span>
                <span
                  className="text-xs px-2 py-0.5 rounded shrink-0 ml-2"
                  style={getStatusBadgeStyle(item.statusColor)}
                >
                  {item.statusName}
                </span>
              </div>
              <div className="text-xs text-gray-500 space-y-0.5">
                <div className="flex flex-wrap gap-1">
                  <span className="bg-primary-100 text-primary-700 text-xs px-2 py-0.5 rounded">
                    {item.vendorCategoryName}
                  </span>
                </div>
                <div className="flex items-center gap-3 mt-1">
                  {item.vendorName && <span>{item.vendorName}</span>}
                  <span className="font-medium text-gray-900">{formatCurrency(item.price)}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span>
                    Paid: <span className="text-gray-700">{formatCurrency(item.totalPaid)}</span>
                  </span>
                  <span>
                    Balance:{' '}
                    <span className={item.balance > 0 ? 'text-red-600' : 'text-green-600'}>
                      {formatCurrency(item.balance)}
                    </span>
                  </span>
                </div>
                {item.dueDate && (
                  <div
                    className={isOverdue(item.dueDate) && item.balance > 0 ? 'text-red-600' : ''}
                  >
                    Due: {formatDate(item.dueDate)}
                  </div>
                )}
                <div className="flex items-center gap-1">
                  <ExpenseAttachmentButton expenseItemId={item.id} size={14} />
                  {onZoomItems && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onZoomItems(item);
                      }}
                      className="text-gray-400 hover:text-primary-600 p-1"
                      aria-label="View itemized list"
                      title="View linked planning items"
                    >
                      <ZoomIn size={14} />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </>
    );
  }

  return (
    <>
      <div className="overflow-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200 text-left text-gray-500">
              {showEventName && <th className="pb-3 pr-4 font-medium">Event</th>}
              <th className="pb-3 pr-4 font-medium">Category</th>
              <th className="pb-3 pr-4 font-medium">Item</th>
              <th className="pb-3 pr-4 font-medium">Vendor</th>
              <th className="pb-3 pr-4 font-medium text-right">Price</th>
              <th className="pb-3 pr-4 font-medium text-right">Paid</th>
              <th className="pb-3 pr-4 font-medium text-right">Balance</th>
              <th className="pb-3 pr-4 font-medium">Status</th>
              <th className="pb-3 pr-4 font-medium">Client</th>
              <th className="pb-3 pr-4 font-medium">Due Date</th>
              <th className="pb-3 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr
                key={item.id}
                onClick={() => onSelect(item.id)}
                className={`border-b border-gray-100 cursor-pointer transition-colors ${
                  selectedId === item.id ? 'bg-primary-50' : 'hover:bg-gray-50'
                }`}
              >
                {showEventName && (
                  <td className="py-3 pr-4 text-gray-700">
                    <div className="flex items-center gap-2">
                      {item.eventLogoUrl && (
                        <img
                          src={item.eventLogoUrl}
                          alt=""
                          className="w-6 h-6 rounded object-cover border border-gray-200 bg-white flex-shrink-0"
                        />
                      )}
                      <span className="truncate">{item.eventName || '—'}</span>
                    </div>
                  </td>
                )}
                <td className="py-3 pr-4">
                  <span className="bg-primary-100 text-primary-700 text-xs px-2 py-0.5 rounded">
                    {item.vendorCategoryName}
                  </span>
                </td>
                <td className="py-3 pr-4 font-medium">{item.itemName || '—'}</td>
                <td className="py-3 pr-4 text-gray-600">{item.vendorName || '—'}</td>
                <td className="py-3 pr-4 text-right text-gray-900">{formatCurrency(item.price)}</td>
                <td className="py-3 pr-4 text-right text-gray-600">
                  {formatCurrency(item.totalPaid)}
                </td>
                <td className="py-3 pr-4 text-right">
                  <span
                    className={item.balance > 0 ? 'text-red-600 font-medium' : 'text-green-600'}
                  >
                    {formatCurrency(item.balance)}
                  </span>
                </td>
                <td className="py-3 pr-4">
                  <span
                    className="text-xs px-2 py-0.5 rounded"
                    style={getStatusBadgeStyle(item.statusColor)}
                  >
                    {item.statusName}
                  </span>
                </td>
                <td className="py-3 pr-4">
                  {(() => {
                    const badge = getClientStatusBadge(item);
                    if (!badge) return <span className="text-xs text-gray-400">—</span>;
                    return (
                      <span
                        className={`text-xs px-2 py-0.5 rounded ${badge.className}`}
                        title={badge.title}
                      >
                        {badge.label}
                      </span>
                    );
                  })()}
                </td>
                <td
                  className={`py-3 pr-4 ${
                    isOverdue(item.dueDate) && item.balance > 0 ? 'text-red-600' : 'text-gray-600'
                  }`}
                >
                  {formatDate(item.dueDate)}
                </td>
                <td className="py-3">
                  <div className="flex items-center gap-1">
                    <ExpenseAttachmentButton expenseItemId={item.id} size={16} />
                    {onZoomItems && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onZoomItems(item);
                        }}
                        className="text-gray-400 hover:text-primary-600 p-1"
                        aria-label="View itemized list"
                        title="View linked planning items"
                      >
                        <ZoomIn size={16} />
                      </button>
                    )}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onSelect(item.id);
                      }}
                      className="text-gray-400 hover:text-primary-600 p-1"
                      aria-label="Edit expense"
                      title="Edit expense"
                    >
                      <Pencil size={16} />
                    </button>
                    {currentUserIsAdmin && (
                      <button
                        onClick={async (e) => {
                          e.stopPropagation();
                          if (
                            await confirm({
                              title: 'Delete Expense',
                              message: 'Delete this expense item? This action cannot be undone.',
                              confirmLabel: 'Delete',
                            })
                          ) {
                            onDelete?.(item.id);
                          }
                        }}
                        className="text-gray-400 hover:text-red-600 p-1"
                        aria-label="Delete expense"
                        title="Delete expense"
                      >
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
